import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { DataGrid } from "@mui/x-data-grid";
import {
  getGroupBySetupToken,
  getSetupReviewees,
  createSetupReviewee,
  getSetupReviewers,
  createSetupReviewer,
  submitSetup,
} from "../../axios/axiosFunctions";

const defaultReviewee = {
  firstName: "",
  lastName: "",
  email: "",
};

const defaultReviewer = {
  firstName: "",
  lastName: "",
  email: "",
  type: "",
};

// Reviewee table columns (no admin actions — click name to open reviewers)
const revieweeColumns = [
  { field: "firstName", headerName: "First Name", flex: 1 },
  { field: "lastName", headerName: "Last Name", flex: 1 },
  { field: "email", headerName: "Email", flex: 1 },
  {
    field: "reviewerCount",
    headerName: "Reviewers",
    flex: 0.5,
    align: "center",
    headerAlign: "center",
  },
];

// Reviewer table columns
const reviewerColumns = [
  { field: "firstName", headerName: "First Name", flex: 1 },
  { field: "lastName", headerName: "Last Name", flex: 1 },
  { field: "email", headerName: "Email", flex: 1 },
  {
    field: "rol",
    headerName: "Type",
    flex: 1,
    valueFormatter: (value) => {
      switch (value) {
        case "manager":
          return "Manager";
        case "direct":
          return "Direct Report";
        case "peer":
          return "Peer";
        case "otherColleague":
          return "Other Colleague";
        default:
          return "—";
      }
    },
  },
];

const ClientSetup = () => {
  const { token } = useParams();

  // Group state
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [error, setError] = useState("");
  const isSimple360 = group?.category === "simple360";

  // Reviewee list state
  const [reviewees, setReviewees] = useState([]);
  const [loadingReviewees, setLoadingReviewees] = useState(false);

  // Reviewer list state (nested view)
  const [reviewerDialogOpen, setReviewerDialogOpen] = useState(false);
  const [currentReviewee, setCurrentReviewee] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(false);

  // Create reviewee dialog
  const [createRevieweeOpen, setCreateRevieweeOpen] = useState(false);
  const [revieweeForm, setRevieweeForm] = useState(defaultReviewee);
  const [revieweeMsg, setRevieweeMsg] = useState("");
  const [revieweeMsgSeverity, setRevieweeMsgSeverity] = useState("error");
  const [showRevieweeMsg, setShowRevieweeMsg] = useState(false);

  // Create reviewer dialog
  const [createReviewerOpen, setCreateReviewerOpen] = useState(false);
  const [reviewerForm, setReviewerForm] = useState(defaultReviewer);
  const [reviewerMsg, setReviewerMsg] = useState("");
  const [reviewerMsgSeverity, setReviewerMsgSeverity] = useState("error");
  const [showReviewerMsg, setShowReviewerMsg] = useState(false);

  // Submit state
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Launch date
  const [launchDate, setLaunchDate] = useState("");

  // =====================
  // LOAD GROUP
  // =====================
  useEffect(() => {
    const fetchGroup = async () => {
      const res = await getGroupBySetupToken(token);
      if (res.group) {
        setGroup(res.group);
        setSubmitted(res.group.submitted || false);
        if (res.group.launchDate) {
          setLaunchDate(res.group.launchDate.slice(0, 10));
        }
      } else {
        setError(res.msg || "Invalid setup link");
      }
      setLoading(false);
    };
    fetchGroup();
  }, [token]);

  // =====================
  // LOAD REVIEWEES
  // =====================
  useEffect(() => {
    if (group) {
      fetchReviewees();
    }
  }, [group]);

  const fetchReviewees = async () => {
    setLoadingReviewees(true);
    const res = await getSetupReviewees(token);
    setReviewees(
      (res.reviewees || []).map((r) => ({
        id: r._id,
        ...r,
      }))
    );
    setLoadingReviewees(false);
  };

  // =====================
  // LOAD REVIEWERS (for selected reviewee)
  // =====================
  const openReviewers = async (reviewee) => {
    setCurrentReviewee(reviewee);
    setReviewerDialogOpen(true);
    setLoadingReviewers(true);
    const res = await getSetupReviewers(token, reviewee._id);
    setReviewers(
      (res.reviewers || []).map((r) => ({
        id: r._id,
        ...r,
      }))
    );
    setLoadingReviewers(false);
  };

  const refreshReviewers = async () => {
    if (!currentReviewee) return;
    const res = await getSetupReviewers(token, currentReviewee._id);
    setReviewers(
      (res.reviewers || []).map((r) => ({
        id: r._id,
        ...r,
      }))
    );
  };

  // =====================
  // CREATE REVIEWEE
  // =====================
  const handleRevieweeFormChange = (e) => {
    setRevieweeForm({ ...revieweeForm, [e.target.name]: e.target.value });
  };

  const handleCreateReviewee = async () => {
    const { firstName, lastName, email } = revieweeForm;
    if (!firstName || !lastName || !email) {
      setRevieweeMsg("All fields are required");
      setRevieweeMsgSeverity("error");
      setShowRevieweeMsg(true);
      setTimeout(() => setShowRevieweeMsg(false), 3000);
      return;
    }

    const res = await createSetupReviewee(token, revieweeForm);
    if (!res.user) {
      setRevieweeMsg(res.msg || "Error creating reviewee");
      setRevieweeMsgSeverity("error");
      setShowRevieweeMsg(true);
      setTimeout(() => setShowRevieweeMsg(false), 3000);
      return;
    }

    // Close reviewee dialog and open reviewers for the new user
    setCreateRevieweeOpen(false);
    setRevieweeForm(defaultReviewee);
    fetchReviewees();
    openReviewers(res.user);
  };

  // =====================
  // CREATE REVIEWER
  // =====================
  const handleReviewerFormChange = (e) => {
    setReviewerForm({ ...reviewerForm, [e.target.name]: e.target.value });
  };

  const handleCreateReviewer = async () => {
    const { firstName, lastName, email, type } = reviewerForm;
    if (!firstName || !lastName || !email || !type) {
      setReviewerMsg("All fields are required");
      setReviewerMsgSeverity("error");
      setShowReviewerMsg(true);
      setTimeout(() => setShowReviewerMsg(false), 3000);
      return;
    }

    if (isSimple360 && reviewers.length >= 12) {
      setReviewerMsg("A feedback receiver can have at most 12 feedback providers.");
      setReviewerMsgSeverity("error");
      setShowReviewerMsg(true);
      setTimeout(() => setShowReviewerMsg(false), 4000);
      return;
    }

    const res = await createSetupReviewer(
      token,
      currentReviewee._id,
      reviewerForm,
    );
    if (!res.user) {
      setReviewerMsg(res.msg || "Error creating reviewer");
      setReviewerMsgSeverity("error");
      setShowReviewerMsg(true);
      setTimeout(() => setShowReviewerMsg(false), 3000);
      return;
    }

    setReviewerMsg("Reviewer created successfully");
    setReviewerMsgSeverity("success");
    setShowReviewerMsg(true);
    setTimeout(() => {
      setShowReviewerMsg(false);
      setCreateReviewerOpen(false);
      setReviewerForm(defaultReviewer);
      refreshReviewers();
      fetchReviewees(); // Update reviewer count
    }, 1500);
  };

  // =====================
  // SUBMIT SETUP
  // =====================
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    const res = await submitSetup(token, { launchDate: launchDate || undefined });
    if (res.error) {
      setSubmitError(res.msg);
      setSubmitting(false);
      return;
    }
    setSubmitted(true);
    setSubmitting(false);
    setSubmitDialogOpen(false);
  };

  // =====================
  // CELL CLICK → open reviewers
  // =====================
  const handleCellClick = (params) => {
    if (submitted) return;
    if (
      params.field === "firstName" ||
      params.field === "lastName" ||
      params.field === "email"
    ) {
      openReviewers(params.row);
    }
  };

  // =====================
  // LOADING / ERROR STATES
  // =====================
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: "center" }}>
        <Typography variant="h5" color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  // =====================
  // SUBMITTED STATE
  // =====================
  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: "center" }}>
        <Typography variant="h4" color="primary" gutterBottom>
          Setup Submitted
        </Typography>
        <Typography variant="h6" color="textSecondary" sx={{ mt: 1 }}>
          {group?.company?.name} — {group?.name}
        </Typography>
        <Typography variant="body1" sx={{ mt: 3 }}>
          Your 360 assessment setup has been submitted successfully. The admin team
          will review your submission and you will be notified when the assessment is
          launched.
        </Typography>
        {launchDate && (
          <Typography variant="body1" sx={{ mt: 2 }}>
            Requested launch date: <strong>{launchDate}</strong>
          </Typography>
        )}
      </Container>
    );
  }

  // =====================
  // MAIN RENDER
  // =====================
  return (
    <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
      {/* HEADER */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4">360 Assessment Setup</Typography>
        <Typography variant="h6" color="textSecondary" sx={{ mt: 1 }}>
          {group?.company?.name} — {group?.name}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* REVIEWEE LIST */}
      <Typography variant="h6" sx={{ textAlign: "center", mb: 2 }}>
        REVIEWEE LIST
      </Typography>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ textAlign: "center", mb: 2 }}
      >
        Click on a reviewee's name to manage their reviewers.
      </Typography>

      <Box sx={{ width: "100%", mb: 3 }}>
        {loadingReviewees ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <DataGrid
            rows={reviewees}
            columns={revieweeColumns}
            onCellClick={handleCellClick}
            autoHeight
            disableRowSelectionOnClick
            hideFooter={reviewees.length <= 10}
            sx={{
              "& .MuiDataGrid-cell:hover": {
                cursor: "pointer",
              },
            }}
          />
        )}
      </Box>

      {/* ADD NEW REVIEWEE */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setCreateRevieweeOpen(true)}
        >
          ADD NEW REVIEWEE
        </Button>
      </Box>

      {/* LAUNCH DATE */}
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          LAUNCH DATE
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Set the desired launch date for the 360 assessment.
        </Typography>
        <TextField
          type="date"
          value={launchDate}
          onChange={(e) => setLaunchDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 250 }}
        />
      </Box>

      {/* SUBMIT BUTTON */}
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          onClick={() => setSubmitDialogOpen(true)}
          disabled={reviewees.length === 0}
          sx={{ px: 6, py: 1.5, fontSize: "1rem" }}
        >
          SUBMIT
        </Button>
        {reviewees.length === 0 && (
          <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
            Add at least one reviewee to submit.
          </Typography>
        )}
      </Box>

      {/* ================================ */}
      {/* SUBMIT CONFIRMATION DIALOG       */}
      {/* ================================ */}
      <Dialog open={submitDialogOpen} onClose={() => !submitting && setSubmitDialogOpen(false)}>
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to submit this setup? Once submitted, you will
            not be able to make further changes.
          </Typography>
          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSubmitDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================ */}
      {/* CREATE REVIEWEE DIALOG           */}
      {/* ================================ */}
      <Dialog
        open={createRevieweeOpen}
        onClose={() => setCreateRevieweeOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
            ADD NEW REVIEWEE
          </Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Typography variant="subtitle1">First Name</Typography>
              <TextField
                name="firstName"
                value={revieweeForm.firstName}
                onChange={handleRevieweeFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle1">Last Name</Typography>
              <TextField
                name="lastName"
                value={revieweeForm.lastName}
                onChange={handleRevieweeFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle1">Email</Typography>
              <TextField
                name="email"
                value={revieweeForm.email}
                onChange={handleRevieweeFormChange}
                fullWidth
                size="small"
              />
            </Grid>

            {showRevieweeMsg && (
              <Grid size={12}>
                <Alert severity={revieweeMsgSeverity}>{revieweeMsg}</Alert>
              </Grid>
            )}

            <Grid size={12} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button variant="contained" onClick={handleCreateReviewee}>
                CREATE
              </Button>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => {
                  setCreateRevieweeOpen(false);
                  setRevieweeForm(defaultReviewee);
                }}
              >
                CANCEL
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* ================================ */}
      {/* REVIEWERS LIST DIALOG            */}
      {/* ================================ */}
      <Dialog
        open={reviewerDialogOpen}
        onClose={() => setReviewerDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            REVIEWERS LIST
          </Typography>
          {currentReviewee && (
            <Typography
              variant="subtitle1"
              color="textSecondary"
              sx={{ textAlign: "center", mb: 1 }}
            >
              for {currentReviewee.firstName} {currentReviewee.lastName}
            </Typography>
          )}
          <Divider sx={{ mb: 2 }} />
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: "flex-start", mb: 2 }}
          >
            {isSimple360
              ? "Add up to 12 feedback providers for this feedback receiver."
              : "Add as many reviewers as needed to ensure comprehensive and well-rounded feedback for this reviewee."}
          </Typography>


          {loadingReviewers ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <DataGrid
              rows={reviewers}
              columns={reviewerColumns}
              autoHeight
              disableRowSelectionOnClick
              hideFooter={reviewers.length <= 10}
            />
          )}

          {/* ADD REVIEWER */}
          <Box sx={{ textAlign: "center", mt: 3, mb: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setCreateReviewerOpen(true)}
              disabled={isSimple360 && reviewers.length >= 12}
            >
              CREATE REVIEWER
            </Button>
            {isSimple360 && reviewers.length >= 12 && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                Maximum of 12 feedback providers reached.
              </Typography>
            )}
          </Box>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => setReviewerDialogOpen(false)}
            >
              CLOSE
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ================================ */}
      {/* CREATE REVIEWER DIALOG           */}
      {/* ================================ */}
      <Dialog
        open={createReviewerOpen}
        onClose={() => setCreateReviewerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h5" color="primary" sx={{ mb: 2 }}>
            CREATE REVIEWER
          </Typography>
          {currentReviewee && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Adding reviewer for: {currentReviewee.firstName}{" "}
              {currentReviewee.lastName}
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid size={6}>
              <Typography variant="subtitle1">First Name</Typography>
              <TextField
                name="firstName"
                value={reviewerForm.firstName}
                onChange={handleReviewerFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle1">Last Name</Typography>
              <TextField
                name="lastName"
                value={reviewerForm.lastName}
                onChange={handleReviewerFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle1">Email</Typography>
              <TextField
                name="email"
                value={reviewerForm.email}
                onChange={handleReviewerFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={6}>
              <Typography variant="subtitle1">Type</Typography>
              <FormControl fullWidth size="small">
                <Select
                  name="type"
                  value={reviewerForm.type}
                  onChange={handleReviewerFormChange}
                >
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="direct">Direct Report</MenuItem>
                  <MenuItem value="peer">Peer</MenuItem>
                  {!isSimple360 && (
                    <MenuItem value="otherColleague">Other Colleague</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {showReviewerMsg && (
              <Grid size={12}>
                <Alert severity={reviewerMsgSeverity}>{reviewerMsg}</Alert>
              </Grid>
            )}

            <Grid size={12} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button variant="contained" onClick={handleCreateReviewer}>
                CREATE
              </Button>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => {
                  setCreateReviewerOpen(false);
                  setReviewerForm(defaultReviewer);
                }}
              >
                CANCEL
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ClientSetup;
